import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { AxiosResponse } from 'axios'
import { UseQueryOptionsWithoutQueryKey } from '../../react-query-config'
import { AggregationLevel, Channel } from './home.model'

interface GetAlertResponse {
  code: string
  value: string | null
}

export interface AlertMessage {
  code: string
  value: string | null
}

export interface GetAlertRequest {
  channel: Channel
  aggregation: AggregationLevel
  storeId: number
  teamId: number | null
}
export const QUERY_KEY_GET_ALERT = 'get-alert'

export const getAlertQueryKey = ({ aggregation, channel, storeId, teamId }: GetAlertRequest) => {
  return [QUERY_KEY_GET_ALERT, aggregation, channel, storeId, aggregation === 'STORE' || !teamId ? null : teamId]
}

export const getAlertEndpoint = ({ aggregation, channel, storeId, teamId }: GetAlertRequest) => {
  const teamParam = teamId ? `&teamId=${teamId}` : ''
  return `/v1/alert?channel=${channel}&aggregation=${aggregation}&storeId=${storeId}${teamParam}`
}

export const hasAlert = async (endpoint: string): Promise<AlertMessage | null> => {
  const response: AxiosResponse<GetAlertResponse> = await axios.get(endpoint)
  return response?.data ?? null
}

export const useApiGetAlert = (
  request: GetAlertRequest,
  enabled: boolean,
  options?: UseQueryOptionsWithoutQueryKey<AlertMessage, Error>
) => {
  const endpoint = getAlertEndpoint(request)
  return useQuery<AlertMessage | null, Error>({
    queryKey: getAlertQueryKey(request),
    queryFn: () => hasAlert(endpoint),
    enabled,
    ...options
  })
}

export const useInvalidateCacheApiGetAlert = () => {
  const queryClient = useQueryClient()

  return (aggregation: AggregationLevel, storeId: number, teamId: number | null) => {
    ;(['INSTORE', 'OMNI'] as Channel[]).forEach(channel => {
      void queryClient.invalidateQueries({
        queryKey: getAlertQueryKey({ aggregation, channel, storeId, teamId })
      })
    })
  }
}
